import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { updateAllHeaderCustomProperties } from '@theme/critical';

/**
 * Desktop megamenu controller — per-link hover + focus open/close.
 *
 * Not a custom element: it's instantiated by {@link HeaderComponent} against the
 * `[data-megamenu-trigger]` nav that lives inside the header bar, so it shares the
 * header's lifecycle and `AbortController`.
 */
class MegaMenu {
  /** @type {HTMLElement} */ #trigger;
  /** @type {HTMLElement | null} */ #panel;
  /** @type {HTMLElement | null} */ #backdrop;
  /** @type {Map<string, HTMLElement>} */ #groups = new Map();
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #closeTimer;

  /**
   * @param {HTMLElement} trigger - The nav element carrying `[data-megamenu-trigger]`.
   * @param {AbortSignal} signal - Tears down listeners when the header disconnects.
   */
  constructor(trigger, signal) {
    this.#trigger = trigger;
    this.#panel = trigger.querySelector('[data-megamenu-panel]');
    if (!this.#panel) return;

    this.#backdrop = document.querySelector('[data-megamenu-backdrop]');
    for (const group of this.#panel.querySelectorAll('[data-megamenu-group-key]')) {
      this.#groups.set(/** @type {HTMLElement} */ (group).dataset.megamenuGroupKey ?? '', group);
    }

    // The panel ships with [hidden] for pre-JS graceful degradation. Once JS runs we
    // drop it so the panel stays display:block and can transition via opacity/visibility
    // (you cannot animate in/out of display:none).
    this.#panel.removeAttribute('hidden');

    this.#init(signal);
  }

  /** @param {AbortSignal} signal */
  #init(signal) {
    const showChevron = this.#trigger.hasAttribute('data-megamenu-chevron');

    for (const li of this.#trigger.querySelectorAll('[data-megamenu-link-key]')) {
      const key = /** @type {HTMLElement} */ (li).dataset.megamenuLinkKey ?? '';
      const hasGroup = this.#groups.has(key);

      if (hasGroup) {
        const link = li.querySelector('a');
        link?.setAttribute('aria-haspopup', 'true');
        link?.setAttribute('aria-controls', 'header-megamenu');
        link?.setAttribute('aria-expanded', 'false');

        // Decorative chevron on links that open a megamenu (opt-in via setting).
        // Rotation is handled in CSS off the existing aria-expanded state.
        if (showChevron && link && !link.querySelector('.header__menu-chevron')) {
          const chevron = document.createElement('span');
          chevron.className = 'header__menu-chevron';
          chevron.setAttribute('aria-hidden', 'true');
          chevron.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
          link.appendChild(chevron);
        }
      }

      li.addEventListener('mouseenter', () => this.#open(hasGroup ? key : null), { signal });
      li.addEventListener('focusin', () => this.#open(hasGroup ? key : null), { signal });
    }

    // Schedule the close on the whole header bar leaving, not just the nav. The nav's box
    // wraps only the links row, but the panel opens flush with the bottom of `.header__inner`
    // (top: 100%), so the centring space + bottom padding below the links is a hover "dead
    // zone" that belongs to neither the nav nor the panel: crossing it slowly fires the leave
    // and closes the menu before the pointer reaches the panel. `.header__inner` contains the
    // links, that gap, AND the panel (a descendant), so leaving it never fires while the
    // pointer travels down into the panel — no dead zone at any speed. Fall back to the nav.
    const hoverZone = this.#trigger.closest('.header__inner') ?? this.#trigger;
    hoverZone.addEventListener('mouseleave', this.#scheduleClose, { signal });
    hoverZone.addEventListener('mouseenter', () => clearTimeout(this.#closeTimer), { signal });
    this.#panel.addEventListener('mouseenter', () => clearTimeout(this.#closeTimer), { signal });
    this.#panel.addEventListener('mouseleave', this.#scheduleClose, { signal });
    this.#trigger.addEventListener(
      'focusout',
      (event) => {
        if (!this.#trigger.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#scheduleClose();
      },
      { signal },
    );
    this.#trigger.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          this.#setActive(null);
          /** @type {HTMLElement} */ (event.target)?.blur();
        }
      },
      { signal },
    );
  }

  /** @param {string | null} key */
  #open(key) {
    clearTimeout(this.#closeTimer);
    this.#setActive(key && this.#groups.has(key) ? key : null);
  }

  #scheduleClose = () => {
    this.#closeTimer = setTimeout(() => this.#setActive(null), 120);
  };

  /** @param {string | null} key */
  #setActive(key) {
    const group = key ? this.#groups.get(key) : null;

    for (const g of this.#groups.values()) g.removeAttribute('data-megamenu-active');
    for (const link of this.#trigger.querySelectorAll('[data-megamenu-link-key] > a')) {
      link.setAttribute('aria-expanded', 'false');
    }

    if (group && key) {
      group.setAttribute('data-megamenu-active', '');
      this.#panel?.setAttribute('data-megamenu-open', '');
      this.#backdrop?.setAttribute('data-megamenu-backdrop-open', '');
      const link = this.#trigger.querySelector(`[data-megamenu-link-key="${CSS.escape(key)}"] > a`);
      link?.setAttribute('aria-expanded', 'true');
    } else {
      this.#panel?.removeAttribute('data-megamenu-open');
      this.#backdrop?.removeAttribute('data-megamenu-backdrop-open');
    }
  }
}

/**
 * The header bar (`.header__container`). Owns:
 * - the desktop megamenu (via {@link MegaMenu}),
 * - transparent → solid colour state,
 * - sticky "hide on scroll down / show on scroll up" behaviour (opt-in),
 * - keeping the `--header-height` / `--header-group-height` custom properties current
 *   after hydration (the runtime half of `critical.js`'s header-height bootstrap).
 *
 * @extends {Component}
 */
class HeaderComponent extends Component {
  #abortController = new AbortController();
  /** @type {HTMLElement | null} */ #section = null;
  /** @type {MegaMenu | undefined} */ #megaMenu;
  /** @type {ResizeObserver | undefined} */ #resizeObserver;
  /** @type {IntersectionObserver | undefined} */ #stickySolidObserver;
  /** @type {IntersectionObserver | undefined} */ #passedObserver;
  #lastScrollY = 0;
  #ticking = false;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section--header');
    const { signal } = this.#abortController;

    const trigger = /** @type {HTMLElement | null} */ (this.querySelector('[data-megamenu-trigger]'));
    if (trigger) this.#megaMenu = new MegaMenu(trigger, signal);

    if (this.#section?.hasAttribute('transparent')) this.#initTransparent(signal);
    if (
      this.#section?.hasAttribute('data-sticky-solid') ||
      this.#section?.hasAttribute('data-sticky-shadow') ||
      this.#section?.hasAttribute('data-sticky-border') ||
      (this.#section?.hasAttribute('sticky') && this.#section?.hasAttribute('transparent'))
    ) {
      this.#initStuckObserver();
    }
    if (this.#section?.hasAttribute('sticky') && this.#section.hasAttribute('data-hide-on-scroll')) {
      this.#initAutoHide(signal);
    }
    if (this.#section?.hasAttribute('transparent') && !this.#section.hasAttribute('sticky')) {
      this.#initPassedObserver();
    }

    this.#initHeightObservers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    this.#stickySolidObserver?.disconnect();
    this.#passedObserver?.disconnect();
  }

  /**
   * Transparent header turns solid while the search modal is open. The modal is a
   * dialog-component rendered outside the header, so observe its open/close via the
   * `dialog:open` / `dialog:close` events. They don't bubble, so listen in the capture
   * phase. (Cart and the menus are handled in CSS via `:has()`.)
   *
   * @param {AbortSignal} signal
   */
  #initTransparent(signal) {
    const section = this.#section;
    if (!section) return;

    /** @param {Event} event */
    const isSearchModal = (event) => event.target instanceof Element && event.target.closest('#search-modal');

    document.addEventListener(
      'dialog:open',
      (event) => {
        if (isSearchModal(event)) section.classList.add('is-solid');
      },
      { capture: true, signal },
    );
    document.addEventListener(
      'dialog:close',
      (event) => {
        if (isSearchModal(event)) section.classList.remove('is-solid');
      },
      { capture: true, signal },
    );
  }

  /**
   * Toggle `.is-stuck` once the header is pinned. A zero-impact sentinel just above the
   * header is watched with an IntersectionObserver; when it scrolls out of view the
   * header is stuck. Drives the transparent "solid when scrolled" colour flip, the
   * optional sticky shadow, and — for transparent sticky headers — gating
   * `--sticky-header-offset` (see the header section stylesheet).
   */
  #initStuckObserver() {
    const section = this.#section;
    if (!section) return;

    const sentinel = document.createElement('div');
    sentinel.style.cssText = 'height:1px;margin-bottom:-1px;width:0;visibility:hidden;pointer-events:none;';
    section.before(sentinel);

    this.#stickySolidObserver = new IntersectionObserver(
      ([entry]) => section.classList.toggle('is-stuck', !entry.isIntersecting),
      { threshold: 0 },
    );
    this.#stickySolidObserver.observe(sentinel);
  }

  /**
   * Toggle `.header--passed` once a non-sticky transparent header has fully scrolled
   * out of view. Sticky page content below starts under the header-group offset so it
   * clears the transparent header UI (see the header section stylesheet); once the
   * header is gone there's nothing left to clear, so the offset releases and the
   * content may hug the viewport top.
   */
  #initPassedObserver() {
    const section = this.#section;
    if (!section) return;

    this.#passedObserver = new IntersectionObserver(
      ([entry]) => section.classList.toggle('header--passed', !entry.isIntersecting),
      { threshold: 0 },
    );
    this.#passedObserver.observe(section);
  }

  /**
   * Sticky "auto-hide": hide the header when scrolling down, reveal it when scrolling
   * up. Opt-in (`data-hide-on-scroll`, only set when the sticky header is enabled).
   *
   * @param {AbortSignal} signal
   */
  #initAutoHide(signal) {
    this.#lastScrollY = window.scrollY;
    window.addEventListener(
      'scroll',
      () => {
        if (this.#ticking) return;
        this.#ticking = true;
        requestAnimationFrame(() => {
          this.#updateAutoHide();
          this.#ticking = false;
        });
      },
      { passive: true, signal },
    );
  }

  #updateAutoHide() {
    const section = this.#section;
    if (!section) return;

    // An overlay locked scrolling by fixing the body. While locked `scrollY` reads 0, and
    // the release restores it in one jump — both look like a fast scroll down. Reveal the
    // bar and return without touching `#lastScrollY`, so the restoring jump nets a zero
    // delta.
    if (document.body.style.position === 'fixed') {
      section.classList.remove('header--hidden');
      return;
    }

    const currentY = window.scrollY;
    const headerHeight = section.offsetHeight || 0;
    const delta = currentY - this.#lastScrollY;

    // Never hide while an overlay is open (any modal dialog, the mobile drawer, or an
    // open megamenu) or while near the top of the page.
    const overlayOpen =
      document.querySelector('dialog[open]') ||
      section.querySelector('[data-mm-drawer-open]') ||
      section.querySelector('[data-megamenu-open]');

    if (overlayOpen || currentY <= headerHeight) {
      section.classList.remove('header--hidden');
    } else if (delta > 4) {
      section.classList.add('header--hidden');
    } else if (delta < -4) {
      section.classList.remove('header--hidden');
    }

    this.#lastScrollY = currentY;
  }

  /**
   * Keep `--header-height` / `--header-group-height` in sync after hydration. critical.js
   * sets them once before paint; header-group sections can change size at runtime (e.g.
   * a dismissible announcement bar, or the theme editor), so re-measure on resize.
   */
  #initHeightObservers() {
    const groupSections = document.querySelectorAll('.shopify-section-group-header-group');
    this.#resizeObserver = new ResizeObserver(() => updateAllHeaderCustomProperties());

    if (groupSections.length) {
      for (const section of groupSections) this.#resizeObserver.observe(section);
    } else if (this.#section) {
      this.#resizeObserver.observe(this.#section);
    }
  }
}

/**
 * The mobile navigation drawer (`#mobile-menu-drawer`). A full-viewport overlay with a
 * sticky top bar and a multi-panel sliding navigation stack. Modal while open: locks
 * background scroll and traps focus.
 *
 * @extends {Component}
 */
class MenuDrawer extends Component {
  #abortController = new AbortController();
  /** @type {HTMLElement | null} */ #toggle = null;
  /** @type {HTMLElement[]} */ #panels = [];
  #activePath = '';
  #previousScrollY = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #closeTimer;

  connectedCallback() {
    super.connectedCallback();

    this.#toggle =
      document.querySelector(`[aria-controls="${this.id}"]`) ?? document.querySelector('.header__mobile-menu-toggle');
    this.#panels = Array.from(this.querySelectorAll('[data-mm-panel]'));

    const { signal } = this.#abortController;

    this.#distributePromos();
    this.#setPanelStates('');

    this.#toggle?.addEventListener('click', this.#onToggleClick, { signal });
    this.addEventListener('click', this.#onDrawerClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#closeTimer);
  }

  get isOpen() {
    return this.hasAttribute('data-mm-drawer-open');
  }

  open() {
    this.#previousScrollY = window.scrollY;

    // Lock background scroll using the project's standard pattern (see dialog.js): the
    // body becomes fixed-positioned so it can't scroll; the negative top preserves the
    // visual scroll position behind the drawer.
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;

    this.removeAttribute('hidden');
    // Force layout so the next frame animates from the closed state.
    void this.offsetHeight;
    this.setAttribute('data-mm-drawer-open', '');
    this.setAttribute('aria-modal', 'true');
    this.#toggle?.setAttribute('aria-expanded', 'true');

    trapFocus(this);
  }

  close = () => {
    this.removeAttribute('data-mm-drawer-open');
    this.setAttribute('aria-modal', 'false');
    this.#toggle?.setAttribute('aria-expanded', 'false');

    removeTrapFocus();

    // Release the scroll lock and restore the previous scroll position.
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#toggle?.focus();

    // Reset to root after the slide-out completes.
    this.#closeTimer = setTimeout(() => {
      if (!this.isOpen) {
        this.setAttribute('hidden', '');
        this.#setPanelStates('');
      }
    }, 300);
  };

  #onToggleClick = () => {
    if (this.isOpen) this.close();
    else this.open();
  };

  /** @param {MouseEvent} event */
  #onDrawerClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const opener = target.closest('[data-mm-open]');
    if (opener instanceof HTMLElement) {
      event.preventDefault();
      this.#setPanelStates(opener.dataset.mmOpen ?? '');
      return;
    }

    if (target.closest('[data-mm-back]')) {
      event.preventDefault();
      const slash = this.#activePath.lastIndexOf('/');
      this.#setPanelStates(slash > -1 ? this.#activePath.slice(0, slash) : '');
      return;
    }

    // The close button, account link and localization trigger all close the drawer.
    if (target.closest('[data-mm-close-drawer]')) this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  /** @param {string} path */
  #setPanelStates(path) {
    this.#activePath = path;
    for (const panel of this.#panels) {
      const key = panel.dataset.mmKey ?? '';
      if (key === path) panel.dataset.mmState = 'active';
      else if (key === '' || path.startsWith(`${key}/`)) panel.dataset.mmState = 'behind';
      else panel.dataset.mmState = 'ahead';
    }
  }

  /**
   * Distribute mobile menu blocks from the section pool into their matching panels. A
   * block's `data-menu-position` matches a panel's `data-mm-index` exactly ("1", "1.3");
   * otherwise its `data-menu-link` matches a panel whose key equals it, or whose last path
   * segment equals it (e.g. "shoes" matches the "products/shoes" panel). An empty
   * `data-menu-link` targets the root panel. `data-mm-placement` positions the block
   * within that panel: before the menu, or after the menu. On the root panel "after the
   * menu" sits just above the actions list (account/search), keeping those pinned to the
   * bottom; on a submenu panel (no actions) it falls to the end of the panel.
   * `data-mm-hide-links` drops the panel's link list so the block is the whole panel.
   */
  #distributePromos() {
    /** @type {Map<string, HTMLElement>} */ const panelsByKey = new Map();
    /** @type {Map<string, HTMLElement>} */ const panelsByIndex = new Map();
    for (const panel of this.#panels) {
      panelsByKey.set(panel.dataset.mmKey ?? '', panel);
      // Position path ("1", "1.3") — the locale-proof way to target a panel, since a
      // link's title changes per language but its position does not.
      if (panel.dataset.mmIndex) panelsByIndex.set(panel.dataset.mmIndex, panel);
    }

    /** @param {string} linkKey */
    const findPanelForLink = (linkKey) => {
      if (!linkKey) return panelsByKey.get('');
      if (panelsByKey.has(linkKey)) return panelsByKey.get(linkKey);
      for (const [panelKey, panel] of panelsByKey) {
        if (panelKey.endsWith(`/${linkKey}`)) return panel;
      }
      return null;
    };

    for (const el of document.querySelectorAll('[data-mobile-promo]')) {
      const position = /** @type {HTMLElement} */ (el).dataset.menuPosition;
      const linkKey = /** @type {HTMLElement} */ (el).dataset.menuLink || '';
      // Position targeting wins when present; blocks that target by title (and every
      // existing Mobile menu block) fall through to handle matching.
      const panel = position ? panelsByIndex.get(position) : findPanelForLink(linkKey);
      if (!panel) {
        el.remove();
        continue;
      }

      const placement = /** @type {HTMLElement} */ (el).dataset.mmPlacement || 'after_menu';
      const nav = panel.querySelector('.mobile-menu__nav');
      const actions = panel.querySelector('.mobile-menu__actions');

      if (placement === 'before_menu' && nav) {
        panel.insertBefore(el, nav);
      } else if (actions) {
        // after_menu (root panel): between the menu and the actions list, so the
        // actions stay pinned to the bottom.
        panel.insertBefore(el, actions);
      } else {
        // after_menu (submenu panel, no actions): the end of the panel.
        panel.appendChild(el);
      }

      // The block owns the whole panel: drop the link list entirely. Native `hidden`
      // rather than a class, so no CSS is involved.
      if (el.hasAttribute('data-mm-hide-links') && nav instanceof HTMLElement) nav.hidden = true;
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer);
}
